import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, iconLeft, iconRight, id, className, ...props }, ref) => {
    const generatedId = useId();
    const inputId  = id ?? generatedId;
    const errorId  = error ? `${inputId}-error` : undefined;
    const hintId   = hint  ? `${inputId}-hint`  : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-[var(--color-text-primary)]">
            {label}
            {props.required && <span className="text-[var(--color-error)] ml-1" aria-hidden="true">*</span>}
          </label>
        )}
        <div className="relative">
          {iconLeft && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] pointer-events-none" aria-hidden="true">
              {iconLeft}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={cn(
              'w-full px-3 py-2.5 text-sm rounded-[var(--radius-md)]',
              'bg-[var(--color-bg-tertiary)] border text-[var(--color-text-primary)]',
              'placeholder:text-[var(--color-text-secondary)]',
              'transition-colors',
              error
                ? 'border-[var(--color-error)] focus:border-[var(--color-error)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-error)_20%,transparent)]'
                : 'border-[var(--color-border)] focus:border-[var(--color-accent-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-accent-primary)_20%,transparent)]',
              iconLeft  && 'pl-9',
              iconRight && 'pr-9',
              className,
            )}
            {...props}
          />
          {iconRight && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" aria-hidden="true">
              {iconRight}
            </div>
          )}
        </div>
        {hint && !error && (
          <p id={hintId} className="text-xs text-[var(--color-text-secondary)]">{hint}</p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-[var(--color-error)]">{error}</p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';
